import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MeetingDetail } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatWhen } from '../events/shared';
import { useMe } from '../session/SessionProvider';
import { whatsappShare } from '../../shared/whatsapp';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

/** Una reunión: datos, quién lleva qué y peticiones de oración. */
export function MeetingPage() {
  const { meetingId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/organization/churches/${churchId}/meetings/${meetingId}`;
  const data = useAsync((signal) => (churchId ? api.get<MeetingDetail>(base, signal) : Promise.resolve(null)), [base]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      data.reload();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const m = data.data;
  if (!m) return null;

  return (
    <div className="page">
      <p className="small">
        <Link to={`/app/ministerios/${m.ministry.id}`}>← {m.ministry.name}</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>{m.topic}</h1>
          <p className="muted">
            {[m.ministry, ...(m.withMinistries ?? [])].map((x) => x.name).join(' + ')} · {m.cell ? `Célula ${m.cell.name}` : m.withMinistries?.length ? 'Reunión combinada' : 'Todo el ministerio'} {m.canceled ? <span className="badge">Cancelada</span> : null}{m.isPublic && !m.canceled ? <span className="badge">Pública</span> : null}
          </p>
        </div>
        <div className="actions">
          {!m.canceled ? (
            <a
              className="btn btn--secondary"
              href={whatsappShare(
                [`*Reunión de ${[m.ministry, ...(m.withMinistries ?? [])].map((x) => x.name).join(' + ')}*`, m.topic, formatWhen(m.startsAt, m.endsAt), [m.placeName, m.address].filter(Boolean).join(', '), m.mapsUrl, `${window.location.origin}/app/reuniones/${m.id}`]
                  .filter(Boolean)
                  .join('\n'),
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              Enviar por WhatsApp
            </a>
          ) : null}
          {m.canManage ? (
            <Link className="btn btn--secondary" to={`/app/reuniones/${m.id}/editar`}>
              Editar
            </Link>
          ) : null}
        </div>
      </header>
      {error ? <Alert>{error}</Alert> : null}

      <Card title="Detalles">
        <dl className="details">
          <dt>Cuándo</dt>
          <dd>{formatWhen(m.startsAt, m.endsAt)}</dd>
          <dt>Tema</dt>
          <dd>{m.topic}</dd>
          <dt>Lo da</dt>
          <dd>{m.speaker ? m.speaker.name : <span className="muted">Sin definir</span>}</dd>
          <dt>Encargado</dt>
          <dd>{m.host ? m.host.name : <span className="muted">Sin definir</span>}</dd>
          {m.placeName || m.address ? (
            <>
              <dt>Dónde</dt>
              <dd>
                {m.placeName ? <strong>{m.placeName}</strong> : null}
                {m.address ? <span className="block">{m.address}</span> : null}
                {m.mapsUrl ? (
                  <a href={m.mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                    Cómo llegar
                  </a>
                ) : null}
              </dd>
            </>
          ) : null}
          {m.notes ? (
            <>
              <dt>Notas</dt>
              <dd className="pre-line">{m.notes}</dd>
            </>
          ) : null}
          <dt>Participan</dt>
          <dd>{m.participants.length} {m.participants.length === 1 ? 'persona' : 'personas'}</dd>
        </dl>
      </Card>

      {m.hasFood || m.food.length > 0 ? <FoodCard meeting={m} base={base} run={run} busy={busy} myId={me.personId} /> : null}
      <PrayersCard meeting={m} base={base} run={run} busy={busy} />

      {m.canManage ? (
        <p>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm('¿Cancelar esta reunión?')) void run(() => api.post(`${base}/cancel`, {}));
            }}
          >
            Cancelar reunión
          </Button>
        </p>
      ) : null}
    </div>
  );
}

type Run = (action: () => Promise<unknown>) => Promise<boolean>;

function FoodCard({ meeting: m, base, run, busy, myId }: { meeting: MeetingDetail; base: string; run: Run; busy: boolean; myId: string }) {
  const [item, setItem] = useState('');
  const [personId, setPersonId] = useState('');
  function add(e: FormEvent) {
    e.preventDefault();
    if (!item.trim()) return;
    void run(() => api.post(`${base}/food`, { items: [{ item: item.trim(), personId: personId || null }] })).then((ok) => {
      if (ok) {
        setItem('');
        setPersonId('');
      }
    });
  }
  return (
    <Card title="Comida">
      {m.foodNotes ? <p>{m.foodNotes}</p> : null}
      {m.food.length === 0 ? <p className="muted small">Aún no hay lista de quién lleva qué.</p> : null}
      <ul className="checklist">
        {m.food.map((f) => (
          <li key={f.id} className="checklist__item food-item">
            <div className="checklist__body">
              <strong>{f.item}</strong>
              <span className="small">{f.person ? (f.person.personId === myId ? 'Lo llevas tú' : `Lo lleva ${f.person.name}`) : <span className="muted">Nadie lo lleva todavía</span>}</span>
            </div>
            {m.canManage ? (
              <>
                <label className="visually-hidden" htmlFor={`fp-${f.id}`}>
                  Quién lleva {f.item}
                </label>
                <SearchSelect
                  id={`fp-${f.id}`}
                  value={f.person?.personId ?? ''}
                  disabled={busy}
                  onChange={(v) => void run(() => api.put(`${base}/food/${f.id}`, { personId: v || null }))}
                  emptyLabel="Nadie todavía"
                  options={personOptions(m.candidates)}
                />
                <Button variant="ghost" disabled={busy} aria-label={`Quitar ${f.item}`} onClick={() => void run(() => api.delete(`${base}/food/${f.id}`))}>
                  Quitar
                </Button>
              </>
            ) : f.canClaim ? (
              <Button variant="secondary" disabled={busy} onClick={() => void run(() => api.put(`${base}/food/${f.id}`, { personId: myId }))}>
                Yo lo llevo
              </Button>
            ) : f.canRelease ? (
              <Button variant="ghost" disabled={busy} onClick={() => void run(() => api.put(`${base}/food/${f.id}`, { personId: null }))}>
                Ya no lo llevo
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {m.canManage ? (
        <form className="form__row food-row" onSubmit={add}>
          <div className="field">
            <label htmlFor="food-new">Algo más que llevar</label>
            <input id="food-new" value={item} maxLength={120} onChange={(e) => setItem(e.target.value)} placeholder="Refrescos" />
          </div>
          <div className="field">
            <label htmlFor="food-new-p">La lleva</label>
            <SearchSelect id="food-new-p" value={personId} onChange={setPersonId} emptyLabel="Nadie todavía" options={personOptions(m.candidates)} />
          </div>
          <Button type="submit" variant="secondary" busy={busy} disabled={!item.trim()}>
            Agregar
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

function PrayersCard({ meeting: m, base, run, busy }: { meeting: MeetingDetail; base: string; run: Run; busy: boolean }) {
  const [body, setBody] = useState('');
  return (
    <Card title={`Peticiones de oración (${m.prayers.length})`}>
      {m.prayers.length === 0 ? <p className="muted small">Nadie ha compartido una petición todavía.</p> : null}
      <ul className="list">
        {m.prayers.map((p) => (
          <li key={p.id} className="list__item">
            <span className="agenda__body">
              <span className="pre-line">{p.body}</span>
              <span className="muted small">{p.author.name}</span>
            </span>
            {p.canDelete ? (
              <Button variant="ghost" disabled={busy} aria-label="Quitar petición" onClick={() => void run(() => api.delete(`${base}/prayers/${p.id}`))}>
                Quitar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {!m.canceled ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) void run(() => api.post(`${base}/prayers`, { body: body.trim() })).then((ok) => ok && setBody(''));
          }}
        >
          <div className="field">
            <label htmlFor="prayer-new">Tu petición</label>
            <textarea id="prayer-new" rows={2} maxLength={1000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="¿Por qué oramos en esta reunión?" />
          </div>
          <Button type="submit" busy={busy} disabled={!body.trim()}>
            Compartir petición
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
